import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Shield, Search, Lock, Mail, Trash2, RefreshCw, AlertTriangle, 
  CheckCircle, TrendingUp, Sun, Moon, ArrowRight, Check, Star, 
  HelpCircle, ChevronDown, Award, Globe, Briefcase, ChevronRight
} from "lucide-react";
import axios from "axios";
import { HOME } from "@/constants/testIds";

// Backend API configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// --- Shadcn Component Mocks / Adapters ---
// Since we have the direct files, we can also write clean components inline 
// or import them. Let's write extremely clean Tailwind-styled UI blocks matching 
// the design archetype perfectly.

export function Button({ className, children, ...props }) {
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Input({ className, ...props }) {
  return (
    <input
      className={`flex h-10 w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-slate-900 dark:text-slate-100 ${className}`}
      {...props}
    />
  );
}

export function Textarea({ className, ...props }) {
  return (
    <textarea
      className={`flex min-h-[80px] w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 py-2 text-sm ring-offset-background placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-slate-900 dark:text-slate-100 ${className}`}
      {...props}
    />
  );
}

// Inline SVG brand logo (transparent, no background box)
function LogoIcon({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M32 32 C27 23 18 22 14 28 C10 34 14 42 21 42 C28 42 32 32 32 32 C37 41 46 42 50 36 C54 30 50 22 43 22 C36 22 32 32 32 32 Z" stroke="#ef4444" strokeWidth="4.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx="14.5" cy="28.5" r="1.8" fill="#f87171" />
      <circle cx="49.5" cy="35.5" r="1.8" fill="#f87171" />
      <circle cx="32" cy="32" r="1.6" fill="#fca5a5" />
    </svg>
  );
}


// Custom Accordion Component for FAQs
function AccordionItem({ title, children, isOpen, onClick, id }) {
  return (
    <div className="border-b border-slate-200 dark:border-slate-800 py-4">
      <button
        data-testid={`faq-item-${id}-toggle`}
        onClick={onClick}
        className="flex w-full items-center justify-between text-left font-semibold text-slate-900 dark:text-slate-100 hover:text-red-600 dark:hover:text-red-400 transition-colors"
      >
        <span>{title}</span>
        <ChevronDown className={`h-5 w-5 transform transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Main Page Component
const Home = () => {
  // Theme state
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "dark";
  });

  // Simulator states
  const [scanName, setScanName] = useState("");
  const [isScanning, setIsScanning] = useState(false);
  const [scanStep, setScanNameStep] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [cleanComplete, setCleanComplete] = useState(false);

  // Calculator states
  const [badLinksCount, setBadLinksCount] = useState(3);
  const [placement, setPlacement] = useState("page1");
  const [hasPress, setHasPress] = useState("no");
  const [positiveProfiles, setPositiveProfiles] = useState(1);
  const [calculatedScore, setCalculatedScore] = useState(null);
  const [scoreTier, setScoreTier] = useState("");
  const [calcName, setCalcName] = useState("");
  const [analyzedName, setAnalyzedName] = useState("");

  // Forms states
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState("");
  const [submittingContact, setSubmittingContact] = useState(false);

  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState("");
  const [submittingNewsletter, setSubmittingNewsletter] = useState(false);

  // FAQ Accordion states
  const [openFaq, setOpenFaq] = useState(null);

  // Recent MongoDB database content to display
  const [recentContacts, setRecentContacts] = useState([]);
  const [recentNewsletters, setRecentNewsletters] = useState([]);
  const [showAdminInsights, setShowAdminInsights] = useState(false);

  // Apply Theme
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  // Fetch admin stats to verify persistence
  const fetchAdminStats = async () => {
    try {
      const contactsRes = await axios.get(`${API}/contact`);
      setRecentContacts(contactsRes.data);
      const newsletterRes = await axios.get(`${API}/newsletter`);
      setRecentNewsletters(newsletterRes.data);
    } catch (err) {
      console.error("Error fetching database stats:", err);
    }
  };

  useEffect(() => {
    fetchAdminStats();
  }, []);

  // Run Privacy Scan Simulator
  const handleStartScan = (e) => {
    e.preventDefault();
    if (!scanName.trim()) return;

    setIsScanning(true);
    setScanComplete(false);
    setCleanComplete(false);
    setScanNameStep(1);

    const steps = [
      "Connecting to Google Search index API...",
      "Parsing sentiment analysis logs on Page 1 & Page 2...",
      "Mapping online reputational risks and negative press...",
      "Finalizing comprehensive risk score..."
    ];

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      if (currentStep < steps.length) {
        setScanNameStep(currentStep + 1);
      } else {
        clearInterval(interval);
        setIsScanning(false);
        setScanComplete(true);
      }
    }, 800);
  };

  // Run Cleaning animation
  const handleCleanSlate = () => {
    setIsCleaning(true);
    setTimeout(() => {
      setIsCleaning(false);
      setCleanComplete(true);
      fetchAdminStats(); // Refresh database visual check
    }, 2200);
  };

  // Reset simulator
  const handleResetSimulator = () => {
    setScanName("");
    setScanComplete(false);
    setCleanComplete(false);
    setScanNameStep(0);
  };

  // Handle Score Calculation
  const handleCalculateScore = (e) => {
    e.preventDefault();
    if (!calcName.trim()) return;
    // Formula: Base 100, deduct for bad links, location, news press, add for positive profiles
    let score = 100;
    
    // Negative Links Penalty
    score -= badLinksCount * 7;

    // Placement Penalty
    if (placement === "page1") score -= 25;
    else if (placement === "page2") score -= 12;
    else score -= 4;

    // Press Penalty
    if (hasPress === "yes") score -= 20;

    // Positive Profiles Boost
    score += positiveProfiles * 5;

    // Name exposure factor (deterministic per name)
    const nameKey = calcName.trim().toLowerCase();
    let hash = 0;
    for (let i = 0; i < nameKey.length; i++) {
      hash = (hash * 31 + nameKey.charCodeAt(i)) % 1000;
    }
    score += (hash % 17) - 8; // range -8..+8

    // Constrain score
    const finalScore = Math.max(12, Math.min(100, Math.round(score)));
    setCalculatedScore(finalScore);
    setAnalyzedName(calcName.trim());

    if (finalScore < 45) {
      setScoreTier("Critical Risk Level");
    } else if (finalScore < 75) {
      setScoreTier("Moderate Vulnerability");
    } else {
      setScoreTier("Excellent SEO Health");
    }
  };

  // Handle Contact Submission to MongoDB
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSuccess(false);
    setContactError("");
    
    if (!contactName.trim() || !contactEmail.trim() || !contactPhone.trim() || !contactMessage.trim()) {
      setContactError("All fields are required.");
      return;
    }

    setSubmittingContact(true);
    try {
      const payload = {
        name: contactName,
        email: contactEmail,
        phone: contactPhone,
        message: contactMessage
      };
      await axios.post(`${API}/contact`, payload);
      setContactSuccess(true);
      setContactName("");
      setContactEmail("");
      setContactPhone("");
      setContactMessage("");
      fetchAdminStats(); // Refresh database visual check
    } catch (err) {
      setContactError("Failed to save submission. Please try again.");
      console.error(err);
    } finally {
      setSubmittingContact(false);
    }
  };

  // Handle Newsletter Submission to MongoDB
  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterSuccess(false);
    setNewsletterError("");

    if (!newsletterEmail.trim()) {
      setNewsletterError("Email address is required.");
      return;
    }

    setSubmittingNewsletter(true);
    try {
      const payload = {
        email: newsletterEmail
      };
      await axios.post(`${API}/newsletter`, payload);
      setNewsletterSuccess(true);
      setNewsletterEmail("");
      fetchAdminStats(); // Refresh database visual check
    } catch (err) {
      setNewsletterError("Failed to subscribe. Please try again.");
      console.error(err);
    } finally {
      setSubmittingNewsletter(false);
    }
  };

  return (
    <div className="min-h-screen font-body bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50 transition-colors duration-300">
      
      {/* 1px Border Glassmorphism Header */}
      <header 
        data-testid="header-container"
        className="sticky top-0 z-50 backdrop-blur-xl bg-slate-50/75 dark:bg-slate-950/75 border-b border-slate-200 dark:border-slate-800 transition-colors"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LogoIcon className="h-8 w-8" />
            <a 
              href="/" 
              data-testid="logo-link" 
              className="font-heading font-black text-xl tracking-tighter text-slate-900 dark:text-slate-50"
            >
              CLEANSLATE
            </a>
          </div>

          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" data-testid="features-link" className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 transition-colors">Features</a>
            <a href="#simulator" data-testid="home-link" className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 transition-colors">Scrubber Simulator</a>
            <a href="#calculator" data-testid="calculator-link" className="text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-red-600 dark:hover:text-red-400 transition-colors">Risk Calculator</a>
            {/* Emergent standard Link required by template tests */}
            <a 
              data-testid={HOME.emergentLink} 
              href="https://emergent.sh" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-slate-400 hover:text-slate-200"
            >
              Emergent
            </a>
          </nav>

          <div className="flex items-center gap-4">
            {/* Dynamic Dark Mode Toggle */}
            <button
              data-testid="dark-mode-toggle"
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-2 rounded-lg border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-900 transition-all"
              aria-label="Toggle Dark Mode"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            <a href="#contact">
              <Button 
                data-testid="get-started-cta"
                className="bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 shadow-md font-sans"
              >
                Book Privacy Audit
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Main Hero Section */}
      <section 
        data-testid="hero-container"
        className="relative py-20 lg:py-32 overflow-hidden border-b border-slate-200 dark:border-slate-800"
      >
        {/* Background Network Overlay */}
        <div className="absolute inset-0 pointer-events-none opacity-30 dark:opacity-40 bg-cover bg-center" style={{ backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/d5e701a0-5abc-416a-9ef6-e76ca5a7dd35/images/b94c8e006548b2cdf20fdeaa6ff479ff5d90bcf9d78e1e40cc80eb9226486a9a.png')` }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Hooks & Value Prop */}
          <div className="lg:col-span-6 space-y-8 text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-100/50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 text-red-600 dark:text-red-400">
              <Award className="h-4 w-4" />
              <span className="text-xs font-bold tracking-[0.1em] uppercase">Industry-Leading Content Suppression</span>
            </div>
            
            <h1 
              data-testid="hero-title"
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-none text-slate-900 dark:text-slate-50"
            >
              Clean Your Slate. <br />
              <span className="text-red-600 dark:text-red-500">Own Your Search.</span>
            </h1>
            
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 max-w-xl leading-relaxed">
              Remove damaging articles, outdated links, forum complaints, and misleading reviews from Google. Elevate your online credibility with modern SEO suppression tech built for privacy.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 pt-2">
              <a href="#simulator">
                <Button 
                  data-testid="hero-cta"
                  className="bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 w-full sm:w-auto h-12 px-6 shadow-lg shadow-red-500/10"
                >
                  Test Free Scrubber <ArrowRight className="h-4 w-4" />
                </Button>
              </a>
              <a href="#calculator">
                <Button className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-800 dark:text-slate-200 w-full sm:w-auto h-12 px-6">
                  Check SEO Audit Score
                </Button>
              </a>
            </div>

            <div className="grid grid-cols-3 gap-6 pt-4 border-t border-slate-200 dark:border-slate-800/80">
              <div>
                <p className="text-2xl font-black text-amber-500 dark:text-amber-400 font-heading">98.4%</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Scrub Rate Success</p>
              </div>
              <div>
                <p className="text-2xl font-black text-amber-500 dark:text-amber-400 font-heading">15,000+</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Links Removed</p>
              </div>
              <div>
                <p className="text-2xl font-black text-amber-500 dark:text-amber-400 font-heading">&lt; 14 Days</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">Average Suppression Time</p>
              </div>
            </div>
          </div>

          {/* Right Column: Mini Interactive Scrubber Simulator */}
          <div className="lg:col-span-6" id="simulator">
            <div 
              data-testid="simulator-container"
              className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-2xl p-6 relative overflow-hidden transition-all duration-300"
            >
              {/* Browser Window Header */}
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-200 dark:border-slate-800">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="text-xs text-slate-400 dark:text-slate-500 font-mono bg-slate-100 dark:bg-slate-950 px-3 py-1 rounded-md flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5 text-green-500" /> search-scrubber.io
                </div>
                <div className="w-6" />
              </div>

              {/* Step 1: Input name */}
              {!isScanning && !scanComplete && !cleanComplete && (
                <div className="space-y-6 py-6 text-left">
                  <div className="space-y-2">
                    <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-slate-50">Search Engine Cleanslate Simulator</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                      Enter your name or your brand below to run a simulated privacy scan of Google search index logs.
                    </p>
                  </div>

                  <form onSubmit={handleStartScan} className="space-y-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Name / Brand to Scan</label>
                      <Input
                        data-testid="simulator-search-input"
                        type="text"
                        placeholder="e.g. Johnathan Doe"
                        value={scanName}
                        onChange={(e) => setScanName(e.target.value)}
                        required
                      />
                    </div>
                    <Button
                      data-testid="simulator-scan-btn"
                      type="submit"
                      className="w-full bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 py-3 h-11"
                    >
                      <Search className="h-4 w-4" /> Run Privacy Scan
                    </Button>
                  </form>
                </div>
              )}

              {/* Step 2: Scanning Loading State */}
              {isScanning && (
                <div className="py-12 flex flex-col items-center justify-center space-y-6">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-slate-200 dark:border-slate-800 border-t-red-600 dark:border-t-red-500 animate-spin" />
                    <Search className="h-6 w-6 text-red-600 dark:text-red-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <div className="space-y-2 text-center max-w-sm">
                    <p className="font-semibold text-slate-800 dark:text-slate-200">Scanning search index logs...</p>
                    <div className="text-xs text-red-600 dark:text-red-400 font-mono">
                      {scanStep === 1 && "Connecting to Google Search index API..."}
                      {scanStep === 2 && "Parsing sentiment analysis logs on Page 1 & Page 2..."}
                      {scanStep === 3 && "Mapping online reputational risks..."}
                      {scanStep === 4 && "Finalizing reputation risk scores..."}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Scan Complete, show bad results */}
              {scanComplete && !isCleaning && !cleanComplete && (
                <div className="space-y-4 text-left">
                  <div className="flex items-center justify-between p-3 rounded-xl bg-red-100/50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-xs font-bold text-red-700 dark:text-red-400">Critical Risks Located</p>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400">3 Defamatory links found on Page 1 for &quot;{scanName}&quot;</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold bg-red-200 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-2.5 py-0.5 rounded-full">Scoring: High Risk</span>
                  </div>

                  {/* List of Bad Links */}
                  <div data-testid="negative-results-container" className="space-y-3">
                    <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="text-xs text-red-500 dark:text-red-400 font-mono">www.complaint-board.com &gt; threads</p>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mt-1">&quot;WARNING: Fraud Allegations against {scanName} - Fake reviews&quot;</h4>
                          <p className="text-[11px] text-slate-500 mt-1">Reported by anonymous user. Unverified allegations ranking highly in Google algorithms.</p>
                        </div>
                        <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 px-2 py-0.5 rounded-md shrink-0">De-Index Needed</span>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="text-xs text-red-500 dark:text-red-400 font-mono">www.reputation-wiki.org &gt; list</p>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mt-1">&quot;Why you shouldn&apos;t trust {scanName} - Ripoff report list&quot;</h4>
                          <p className="text-[11px] text-slate-500 mt-1">Negative SEO campaign targeted at your name, artificially boosted to Page 1.</p>
                        </div>
                        <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 px-2 py-0.5 rounded-md shrink-0">De-Index Needed</span>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="text-xs text-red-500 dark:text-red-400 font-mono">www.legal-record-archive.net &gt; mugshots</p>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mt-1">&quot;{scanName} - Outdated 10 Year Dismissed Allegations Archive&quot;</h4>
                          <p className="text-[11px] text-slate-500 mt-1">Outdated dismissed record listing breaching privacy and Right to Be Forgotten policies.</p>
                        </div>
                        <span className="text-[9px] font-bold uppercase bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300 px-2 py-0.5 rounded-md shrink-0">Suppression Needed</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2 flex gap-3">
                    <Button
                      data-testid="simulator-reset-btn"
                      onClick={handleResetSimulator}
                      className="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                    >
                      <RefreshCw className="h-4 w-4" /> Reset
                    </Button>
                    <Button
                      data-testid="simulator-clean-btn"
                      onClick={handleCleanSlate}
                      className="flex-[2] bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 shadow-md shadow-red-500/20"
                    >
                      <Shield className="h-4 w-4" /> De-Index & Clean Slate
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Cleaning animation */}
              {isCleaning && (
                <div className="py-16 flex flex-col items-center justify-center space-y-6">
                  <div className="relative">
                    <div className="w-20 h-26 flex items-center justify-center">
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1, 1.2, 1],
                          rotate: [0, 180, 180, 360, 360],
                          borderRadius: ["20%", "20%", "50%", "50%", "20%"]
                        }}
                        transition={{
                          duration: 2.2,
                          ease: "easeInOut",
                          repeat: Infinity
                        }}
                        className="w-16 h-16 bg-red-500/10 dark:bg-red-500/20 border border-red-500 rounded-xl"
                      />
                      <Shield className="h-8 w-8 text-red-500 absolute" />
                    </div>
                  </div>
                  <div className="space-y-2 text-center max-w-sm">
                    <p className="font-semibold text-slate-800 dark:text-slate-200">Scrubbing defamatory content...</p>
                    <p className="text-xs text-slate-400">Submitting Right to Be Forgotten request signatures and deploying suppression networks.</p>
                  </div>
                </div>
              )}

              {/* Step 5: Clean complete! Positive results */}
              {cleanComplete && (
                <div className="space-y-4 text-left">
                  <div 
                    data-testid="simulator-success-banner"
                    className="p-3 rounded-xl bg-green-100/50 dark:bg-green-950/20 border border-green-200 dark:border-green-900/30 flex items-center gap-2"
                  >
                    <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-green-700 dark:text-green-400">Cleanslate Scrub Complete!</p>
                      <p className="text-[10px] text-slate-500 dark:text-slate-400">Damaging links de-indexed. Professional assets boosted with high authority SEO.</p>
                    </div>
                  </div>

                  {/* Positive replacement search results */}
                  <div data-testid="positive-results-container" className="space-y-3">
                    <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="text-xs text-green-600 dark:text-green-400 font-mono">www.{scanName.toLowerCase().replace(/\s+/g, '')}.com</p>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mt-1">{scanName} | Official Executive Portfolio & Insights</h4>
                          <p className="text-[11px] text-slate-500 mt-1">Fully optimized personal site featuring schema markup and high authority ranking signals.</p>
                        </div>
                        <span className="text-[9px] font-bold bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 px-2 py-0.5 rounded-md shrink-0">Boosted Rank #1</span>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="text-xs text-green-600 dark:text-green-400 font-mono">www.linkedin.com &gt; in &gt; {scanName.toLowerCase().replace(/\s+/g, '')}</p>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mt-1">{scanName} - Executive Profile & Advisory Board</h4>
                          <p className="text-[11px] text-slate-500 mt-1">LinkedIn professional profile with optimized meta descriptions to ensure secure ranking.</p>
                        </div>
                        <span className="text-[9px] font-bold bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 px-2 py-0.5 rounded-md shrink-0">Secure Rank #2</span>
                      </div>
                    </div>

                    <div className="p-3.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/50">
                      <div className="flex justify-between items-start gap-2">
                        <div>
                          <p className="text-xs text-green-600 dark:text-green-400 font-mono">www.tech-conference.org &gt; speakers</p>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100 mt-1">Featured Speaker {scanName} on Innovations in Technology</h4>
                          <p className="text-[11px] text-slate-500 mt-1">Suppressed outdated legal records successfully pushed to deep Page 4+ indexes.</p>
                        </div>
                        <span className="text-[9px] font-bold bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 px-2 py-0.5 rounded-md shrink-0">Protected Rank</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    data-testid="simulator-reset-btn"
                    onClick={handleResetSimulator}
                    className="w-full bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600"
                  >
                    Reset & Test Another Scan
                  </Button>
                </div>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* Feature Bento Grid (Swiss High Contrast style) */}
      <section id="features" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <p className="text-xs font-bold text-red-600 dark:text-red-500 tracking-[0.2em] uppercase">Core Capabilities</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 dark:text-slate-50">
            How Cleanslate Reshapes Your Online Rep
          </h2>
          <p className="text-base text-slate-500 dark:text-slate-400">
            We don’t just bury negative content. We establish high-authority fortress SEO profiles to protect your brand permanently.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          
          {/* Card 1: Right to Be Forgotten */}
          <div className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 space-y-4 transition-all hover:shadow-xl">
            <div className="w-12 h-12 rounded-xl bg-red-100/50 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-500">
              <Lock className="h-6 w-6" />
            </div>
            <h3 className="font-heading font-black text-xl tracking-tight text-slate-900 dark:text-slate-50">Right To Be Forgotten (RTBF)</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              We compile and submit formal privacy, DMCA, and Right to Be Forgotten de-indexing legal signatures to remove links from Google indexes directly.
            </p>
          </div>

          {/* Card 2: Fortress SEO Suppression */}
          <div className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 space-y-4 transition-all hover:shadow-xl">
            <div className="w-12 h-12 rounded-xl bg-red-100/50 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-500">
              <TrendingUp className="h-6 w-6" />
            </div>
            <h3 className="font-heading font-black text-xl tracking-tight text-slate-900 dark:text-slate-50">Fortress SEO Suppression</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              Our automated suppression network deploys and boosts highly authoritative custom portfolios to suppress negative search results to Page 3 or deeper.
            </p>
          </div>

          {/* Card 3: 24/7 Reputation Watchdog */}
          <div className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 space-y-4 transition-all hover:shadow-xl">
            <div className="w-12 h-12 rounded-xl bg-red-100/50 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-500">
              <Shield className="h-6 w-6" />
            </div>
            <h3 className="font-heading font-black text-xl tracking-tight text-slate-900 dark:text-slate-50">24/7 Reputation Watchdog</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              Continuous search result tracking scans Google indexing alerts. If any negative search pattern or complaints emerge, you are notified instantly.
            </p>
          </div>

        </div>
      </section>

      {/* Control Room Risk Assessment & SEO Score Calculator */}
      <section 
        id="calculator"
        className="py-20 bg-slate-100 dark:bg-slate-900/30 border-y border-slate-200 dark:border-slate-800 text-left"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Left Column: Calculator Content */}
            <div className="lg:col-span-5 space-y-6">
              <p className="text-xs font-bold text-red-600 dark:text-red-500 tracking-[0.2em] uppercase">Control Room Audit</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 dark:text-slate-50">
                Determine Your Search Vulnerability
              </h2>
              <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 leading-relaxed">
                Use our proprietary reputation vulnerability engine. Adjust the sliders to configure your current Google Search index risks and calculate your overall SEO rating instantly.
              </p>
              
              <ul className="space-y-3.5">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-red-600 dark:text-red-500 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Evaluate Right to Be Forgotten legal eligibility</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-red-600 dark:text-red-500 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Gauge active SEO authority suppression depth</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-red-600 dark:text-red-500 shrink-0 mt-0.5" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Generate instantly actionable suppress-actions</span>
                </li>
              </ul>
            </div>

            {/* Right Column: Calculator Tool */}
            <div className="lg:col-span-7">
              <div 
                data-testid="calculator-container"
                className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl space-y-8"
              >
                <form onSubmit={handleCalculateScore} className="space-y-6">
                  
                  {/* Name input: the identity being audited */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-semibold text-slate-800 dark:text-slate-200 block">Whose name or brand are we auditing?</label>
                    <Input
                      data-testid="calc-name-input"
                      type="text"
                      placeholder="e.g. Johnathan Doe"
                      value={calcName}
                      onChange={(e) => setCalcName(e.target.value)}
                      required
                    />
                  </div>

                  {/* Slider: Negative Links */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm font-semibold">
                      <label className="text-slate-800 dark:text-slate-200">How many negative search results do you see?</label>
                      <span className="text-red-600 dark:text-red-400 font-mono text-base">{badLinksCount} link{badLinksCount !== 1 ? 's' : ''}</span>
                    </div>
                    <input
                      data-testid="calc-links-slider"
                      type="range"
                      min="0"
                      max="10"
                      className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-lg appearance-none cursor-pointer accent-red-600 dark:accent-red-500"
                      value={badLinksCount}
                      onChange={(e) => setBadLinksCount(parseInt(e.target.value))}
                    />
                  </div>

                  {/* Dropdown/Select: Placement */}
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-800 dark:text-slate-200 block">Where are they primarily located?</label>
                    <select
                      data-testid="calc-placement-select"
                      className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-sm font-medium text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                      value={placement}
                      onChange={(e) => setPlacement(e.target.value)}
                    >
                      <option value="page1">Google Page 1 (Top 10 Results)</option>
                      <option value="page2">Google Page 2 (Results 11-20)</option>
                      <option value="page3">Google Page 3 or Deeper</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Select: Active Press */}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-800 dark:text-slate-200 block">Is there active news press coverage?</label>
                      <select
                        data-testid="calc-press-select"
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-sm font-medium text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                        value={hasPress}
                        onChange={(e) => setHasPress(e.target.value)}
                      >
                        <option value="yes">Yes, active media articles</option>
                        <option value="no">No, standard forums or blogs</option>
                      </select>
                    </div>

                    {/* Select: Positive Profiles */}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-800 dark:text-slate-200 block">Number of existing positive profiles?</label>
                      <select
                        data-testid="calc-profiles-select"
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-sm font-medium text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-red-500"
                        value={positiveProfiles}
                        onChange={(e) => setPositiveProfiles(parseInt(e.target.value))}
                      >
                        <option value="0">0 profiles optimized</option>
                        <option value="1">1 to 2 profiles (LinkedIn/Medium)</option>
                        <option value="3">3 to 4 (Website + Socials)</option>
                        <option value="5">5+ (Highly optimized presence)</option>
                      </select>
                    </div>
                  </div>

                  <Button
                    data-testid="calc-submit-btn"
                    type="submit"
                    className="w-full bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600 text-white h-11 shadow-md shadow-red-500/10"
                  >
                    Calculate Search Vulnerability Score
                  </Button>
                </form>

                {/* Score gauge Output */}
                <AnimatePresence>
                  {calculatedScore !== null && (
                    <motion.div 
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/40 text-left space-y-6"
                    >
                      <div className="flex flex-col sm:flex-row items-center gap-6 justify-between">
                        
                        {/* Interactive gauge representation */}
                        <div 
                          data-testid="calc-score-gauge"
                          className="relative flex items-center justify-center shrink-0"
                        >
                          <svg className="w-24 h-24 transform -rotate-90">
                            <circle
                              cx="48"
                              cy="48"
                              r="40"
                              stroke="currentColor"
                              strokeWidth="8"
                              className="text-slate-200 dark:text-slate-800"
                              fill="transparent"
                            />
                            <circle
                              cx="48"
                              cy="48"
                              r="40"
                              stroke="currentColor"
                              strokeWidth="8"
                              className={`${
                                calculatedScore < 45 ? "text-red-500" :
                                calculatedScore < 75 ? "text-yellow-500" : "text-green-500"
                              }`}
                              strokeDasharray={251.2}
                              strokeDashoffset={251.2 - (251.2 * calculatedScore) / 100}
                              fill="transparent"
                            />
                          </svg>
                          <div className="absolute flex flex-col items-center justify-center text-center">
                            <span className="text-xl font-black font-heading text-slate-900 dark:text-slate-50">{calculatedScore}</span>
                            <span className="text-[10px] uppercase font-bold text-slate-400">SEO Score</span>
                          </div>
                        </div>

                        <div className="space-y-1 text-center sm:text-left flex-1">
                          <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                            calculatedScore < 45 ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" :
                            calculatedScore < 75 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-950 dark:text-yellow-300" :
                            "bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300"
                          }`}>
                            {scoreTier}
                          </span>
                          <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200 mt-2" data-testid="calc-result-name">Search Risk Profile for {analyzedName}:</h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed mt-1">
                            {calculatedScore < 45 ? "Highly critical search vulnerability. Negative links dominate your Google Page 1. Immediate de-indexing actions are recommended to secure your professional reputation." :
                             calculatedScore < 75 ? "Moderate risk. Damaging search results are highly visible. Active suppression SEO campaigns and creation of high-authority web assets should be initiated immediately." :
                             "Healthy score, but vigilance is required. Continued 24/7 monitoring of search engine indexing is advised to prevent future negative search results."}
                          </p>
                        </div>
                      </div>

                      {/* Actionable recommendations checklist */}
                      <div className="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-3">
                        <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Actionable Suppression Roadmap:</p>
                        <ul 
                          data-testid="calc-suggestions-list"
                          className="space-y-2 text-xs text-slate-700 dark:text-slate-300"
                        >
                          {badLinksCount > 0 && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-red-500 shrink-0" /> Submit privacy de-indexing legal complaints to Google index registry.
                            </li>
                          )}
                          {placement === "page1" && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-red-500 shrink-0" /> Launch high-authority personal website with optimized JSON-LD schema.
                            </li>
                          )}
                          {hasPress === "yes" && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-red-500 shrink-0" /> Draft legal cease-and-desist notifications to the publishing media outlets.
                            </li>
                          )}
                          {positiveProfiles < 3 && (
                            <li className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-red-500 shrink-0" /> Optimize high-authority standard profiles (LinkedIn, X, Medium, Crunchbase).
                            </li>
                          )}
                          <li className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-red-500 shrink-0" /> Enable 24/7 de-indexing monitors on search query variations of your brand.
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Testimonials Section with sourced portraits */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <p className="text-xs font-bold text-red-600 dark:text-red-500 tracking-[0.2em] uppercase">Verified Success</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 dark:text-slate-50">
            Backed by Trusted Leaders
          </h2>
          <p className="text-base text-slate-500 dark:text-slate-400">
            Hear from industry leaders and public figures who completely reclaimed their online search results.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Testimonial 1 */}
          <div className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 space-y-6">
            <div className="flex items-center gap-4">
              <img
                src="https://images.pexels.com/photos/12396627/pexels-photo-12396627.jpeg"
                alt="Sarah Jenkins"
                className="w-14 h-14 rounded-full object-cover border border-slate-200 dark:border-slate-700"
              />
              <div>
                <h4 className="font-heading font-bold text-base text-slate-900 dark:text-slate-50">Sarah Jenkins</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">CEO, TechFlow</p>
              </div>
            </div>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed italic">
              &quot;An outdated litigation record from ten years ago was crippling our fundraising rounds. Cleanslate worked tirelessly to get Google to de-index the link within two weeks. Absolutely stellar results.&quot;
            </p>
          </div>

          {/* Testimonial 2 */}
          <div className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 space-y-6">
            <div className="flex items-center gap-4">
              <img
                src="https://images.pexels.com/photos/27086922/pexels-photo-27086922.jpeg"
                alt="David Chen"
                className="w-14 h-14 rounded-full object-cover border border-slate-200 dark:border-slate-700"
              />
              <div>
                <h4 className="font-heading font-bold text-base text-slate-900 dark:text-slate-50">David Chen</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400">Public Advisory Partner</p>
              </div>
            </div>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed italic">
              &quot;We were hit by a malicious forum negative spam attack on Page 1. Cleanslate launched high-authority schema-optimized SEO assets that pushed the bad posts to Page 3 in record time.&quot;
            </p>
          </div>
        </div>
      </section>

      {/* Accordion FAQ Section */}
      <section className="py-20 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-left" id="faq">
        <div className="text-center mb-12 space-y-4">
          <p className="text-xs font-bold text-red-600 dark:text-red-500 tracking-[0.2em] uppercase">Common Questions</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-slate-900 dark:text-slate-50">
            Frequently Answered Concerns
          </h2>
        </div>

        <div data-testid="faq-container" className="space-y-1">
          <AccordionItem
            id="1"
            title="How do you actually remove links from Google Search?"
            isOpen={openFaq === 1}
            onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
          >
            We compile formal legal applications using privacy laws (such as GDPR, Right to Be Forgotten, or CCPA) and submit them directly to Google&apos;s legal department. For copyrighted content, we leverage DMCA takedown requests to ensure immediate suppression and de-indexing.
          </AccordionItem>

          <AccordionItem
            id="2"
            title="What happens if a negative link cannot be de-indexed?"
            isOpen={openFaq === 2}
            onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
          >
            If a site doesn&apos;t qualify for legal de-indexing, we implement an active &quot;Suppression Campaign.&quot; This involves launching 5-10 high-authority personal profiles and customized portfolios that outrank and push the offending links onto Page 2, 3, or deeper, where 95% of searchers never click.
          </AccordionItem>

          <AccordionItem
            id="3"
            title="Is my personal data kept secure?"
            isOpen={openFaq === 3}
            onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
          >
            Absolutely. Cleanslate operates under a strict privacy policy. Any contact details, submitted URLs, or audit details are persisted safely in our secure database models and are never shared or sold to third-party scrapers or agencies.
          </AccordionItem>

          <AccordionItem
            id="4"
            title="How long does the entire de-indexing process take?"
            isOpen={openFaq === 4}
            onClick={() => setOpenFaq(openFaq === 4 ? null : 4)}
          >
            Google legal applications typically resolve within 7-14 days. Custom SEO suppression campaign structures require 4-6 weeks to establish authority and fully suppress defamatory content in indexing lists.
          </AccordionItem>
        </div>
      </section>

      {/* MongoDB Contact & Newsletter forms Section */}
      <section className="py-20 bg-slate-100 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 text-left" id="contact">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            
            {/* Left Side: Contact Form saving to MongoDB */}
            <div className="lg:col-span-7 space-y-6">
              <div className="space-y-3">
                <p className="text-xs font-bold text-red-600 dark:text-red-500 tracking-[0.2em] uppercase">Secure Consultation</p>
                <h2 className="font-heading text-3xl font-black tracking-tighter text-slate-900 dark:text-slate-50">
                  Book a Secure Case Review
                </h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
                  Submit details of the negative content safely. Our privacy consultants will review your case and outline de-indexing options.
                </p>
              </div>

              <div 
                data-testid="contact-form-container"
                className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 shadow-lg"
              >
                {contactSuccess ? (
                  <div 
                    data-testid="contact-success-alert"
                    className="p-6 rounded-xl bg-green-100/50 dark:bg-green-950/20 border border-green-200 dark:border-green-900/30 text-green-700 dark:text-green-400 space-y-2"
                  >
                    <CheckCircle className="h-6 w-6" />
                    <p className="font-bold text-base">Inquiry Saved Successfully!</p>
                    <p className="text-xs leading-relaxed text-slate-500">Your privacy audit inquiry has been stored securely in our MongoDB cluster. A consultant will contact you via email shortly.</p>
                    <Button 
                      onClick={() => setContactSuccess(false)}
                      className="mt-4 bg-green-600 text-white hover:bg-green-700 text-xs py-2 h-8 rounded-md"
                    >
                      Submit Another Case
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    {contactError && <p className="text-xs text-red-500 font-semibold">{contactError}</p>}
                    
                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Name / Brand</label>
                      <Input
                        data-testid="contact-name-input"
                        type="text"
                        placeholder="e.g. Johnathan Doe"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Email Address</label>
                      <Input
                        data-testid="contact-email-input"
                        type="email"
                        placeholder="e.g. jane@company.com"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Phone Number</label>
                      <Input
                        data-testid="contact-phone-input"
                        type="tel"
                        placeholder="e.g. +1 (555) 123-4567"
                        value={contactPhone}
                        onChange={(e) => setContactPhone(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-widest">Case Message & Links</label>
                      <Textarea
                        data-testid="contact-message-input"
                        placeholder="Please paste the negative Google Search URLs and any context details..."
                        value={contactMessage}
                        onChange={(e) => setContactMessage(e.target.value)}
                        required
                      />
                    </div>

                    <Button
                      data-testid="contact-submit-btn"
                      type="submit"
                      disabled={submittingContact}
                      className="w-full bg-red-600 dark:bg-red-500 text-white hover:bg-red-700 dark:hover:bg-red-600 h-11"
                    >
                      {submittingContact ? "Submitting securely..." : "Submit Secure Consultation"}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Right Side: Newsletter subscription & Database Visualizer */}
            <div className="lg:col-span-5 space-y-8 flex flex-col justify-between">
              
              {/* Newsletter subscription form */}
              <div 
                data-testid="newsletter-form-container"
                className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/40 shadow-lg space-y-6"
              >
                <div className="space-y-2">
                  <h3 className="font-heading font-black text-xl tracking-tight text-slate-900 dark:text-slate-50">Reputation Intel Letter</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                    Subscribe to receive Right to Be Forgotten law updates, Google ranking changes, and search engine optimization tips.
                  </p>
                </div>

                {newsletterSuccess ? (
                  <div 
                    data-testid="newsletter-success-alert"
                    className="p-4 rounded-xl bg-green-100/50 dark:bg-green-950/20 border border-green-200 dark:border-green-900/30 text-green-700 dark:text-green-400 text-xs"
                  >
                    <p className="font-bold">Subscribed!</p>
                    <p className="mt-1">You are now registered for search engine updates.</p>
                  </div>
                ) : (
                  <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                    {newsletterError && <p className="text-xs text-red-500 font-semibold">{newsletterError}</p>}
                    <Input
                      data-testid="newsletter-email-input"
                      type="email"
                      placeholder="e.g. privacy-first@gmail.com"
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      required
                    />
                    <Button
                      data-testid="newsletter-submit-btn"
                      type="submit"
                      disabled={submittingNewsletter}
                      className="w-full bg-slate-900 dark:bg-slate-800 text-white hover:bg-slate-800 dark:hover:bg-slate-700"
                    >
                      {submittingNewsletter ? "Registering..." : "Join Newsletter"}
                    </Button>
                  </form>
                )}
              </div>

              {/* Real-time MongoDB Visualizer (Visual validation of database persistence) */}
              <div className="p-8 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-heading font-bold text-sm tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-1.5">
                    <Globe className="h-4 w-4 text-red-500" /> MongoDB Real-Time Verification
                  </h4>
                  <button
                    onClick={() => setShowAdminInsights(!showAdminInsights)}
                    className="text-xs font-semibold text-red-600 dark:text-red-400 hover:underline"
                  >
                    {showAdminInsights ? "Hide Logs" : "Show Logs"}
                  </button>
                </div>
                
                <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
                  Below statistics dynamically query the FastAPI backend (and MongoDB collections) to prove submission persistence works seamlessly.
                </p>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-xl text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Contact Records</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-slate-50 mt-1">{recentContacts.length}</p>
                  </div>
                  <div className="p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-850 rounded-xl text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Newsletter Signups</p>
                    <p className="text-2xl font-black text-slate-900 dark:text-slate-50 mt-1">{recentNewsletters.length}</p>
                  </div>
                </div>

                {showAdminInsights && (
                  <div className="max-h-40 overflow-y-auto space-y-2 border-t border-slate-200 dark:border-slate-800 pt-3 text-xs font-mono text-slate-600 dark:text-slate-400">
                    <p className="font-bold uppercase text-[10px] text-slate-500">Recent DB Writes:</p>
                    {recentContacts.slice(-2).map((c, i) => (
                      <div key={i} className="p-2 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                        <span className="text-green-600 dark:text-green-400">Contact:</span> {c.name} ({c.email}) {c.phone ? `· ${c.phone}` : ""}
                      </div>
                    ))}
                    {recentNewsletters.slice(-2).map((n, i) => (
                      <div key={i} className="p-2 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                        <span className="text-red-600 dark:text-red-400">Newsletter:</span> {n.email}
                      </div>
                    ))}
                    {recentContacts.length === 0 && recentNewsletters.length === 0 && (
                      <p className="text-[11px] italic text-slate-500">No submission records yet. Try submitting a form above!</p>
                    )}
                  </div>
                )}
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Footer Section */}
      <footer className="py-12 border-t border-slate-200 dark:border-slate-800/80 text-sm text-slate-500 dark:text-slate-400 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <LogoIcon className="h-7 w-7" />
            <span className="font-heading font-black tracking-tight text-slate-900 dark:text-slate-100">CLEANSLATE</span>
          </div>
          <p>© 2026 Cleanslate Reputation & SEO. All Rights Reserved. Reclaim your search engine footprint.</p>
          <div className="flex gap-4">
            <a href="#faq" className="hover:underline">Privacy Policy</a>
            <span>•</span>
            <a href="#features" className="hover:underline">Terms of Service</a>
          </div>
        </div>
      </footer>

    </div>
  );
};

// Main App Router Config
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
